<template>
  <div class="edrea_tm_section" id="projects">
    <div class="section_inner">
      <div class="edrea_tm_projects">
        <div class="edrea_tm_main_title" style="text-align: center">
          <h3>
            {{ t.projects.titleLight }}
            <span class="coloring">{{ t.projects.titleColored }}</span>
          </h3>
          <p class="subtitle">{{ t.projects.subtitle }}</p>
        </div>

        <ul class="projects_list">
          <li v-for="(project, index) in t.projects.items" :key="index">
            <div
              class="list_inner"
              :class="{ clickable: project.detail }"
              :role="project.detail ? 'button' : null"
              :tabindex="project.detail ? 0 : null"
              @click="project.detail && openDetail(project)"
              @keydown.enter="project.detail && openDetail(project)"
            >
              <div class="image">
                <div
                  class="main"
                  :style="{ backgroundImage: `url(${project.img})` }"
                ></div>
                <span class="tag">{{ project.tag }}</span>
                <img
                  v-if="project.logo"
                  class="logo_badge"
                  :src="project.logo"
                  :alt="project.title"
                />
              </div>
              <div class="content">
                <h3 class="title">{{ project.title }}</h3>
                <p class="text">{{ project.des }}</p>
                <div class="modules" v-if="project.modules && project.modules.length">
                  <span class="refs_label">{{ project.modulesLabel }}</span>
                  <ul class="modules_list">
                    <li v-for="(mod, m) in project.modules" :key="m">
                      {{ mod }}
                    </li>
                  </ul>
                </div>
                <button
                  v-if="project.detail"
                  type="button"
                  class="detail_btn"
                  @click.stop="openDetail(project)"
                >
                  {{ t.projects.modal.viewDetail }} →
                </button>
                <div class="refs" v-if="project.refs && project.refs.length">
                  <span class="refs_label">{{ t.projects.refsLabel }}</span>
                  <div class="refs_links">
                    <a
                      v-for="(ref, r) in project.refs"
                      :key="r"
                      :href="ref.url"
                      target="_blank"
                      @click.stop
                      rel="noopener"
                      >{{ ref.label }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <Teleport to="body">
      <transition name="pmodal">
        <div
          v-if="active"
          class="project_modal"
          @click.self="closeDetail"
          role="dialog"
          aria-modal="true"
          :aria-label="active.title"
        >
          <div class="pm_box">
            <button
              type="button"
              class="pm_close"
              :aria-label="t.projects.modal.close"
              @click="closeDetail"
            >
              ✕
            </button>

            <header class="pm_head">
              <img
                v-if="active.logo"
                class="pm_logo"
                :src="active.logo"
                :alt="active.title"
              />
              <div>
                <span class="pm_tag">{{ active.tag }}</span>
                <h2 class="pm_title">{{ active.title }}</h2>
                <p class="pm_subtitle">{{ active.detail.subtitle }}</p>
              </div>
            </header>

            <div class="pm_gallery">
              <figure class="pm_main">
                <img
                  :src="active.detail.gallery[slide].img"
                  :alt="active.detail.gallery[slide].caption"
                />
                <figcaption>{{ active.detail.gallery[slide].caption }}</figcaption>
              </figure>
              <div class="pm_thumbs" v-if="active.detail.gallery.length > 1">
                <button
                  v-for="(g, i) in active.detail.gallery"
                  :key="i"
                  type="button"
                  :class="{ current: i === slide }"
                  :aria-label="g.caption"
                  @click="slide = i"
                >
                  <img :src="g.img" alt="" />
                </button>
              </div>
            </div>

            <div class="pm_stats">
              <div v-for="(st, i) in active.detail.stats" :key="i">
                <strong>{{ st.value }}</strong>
                <span>{{ st.label }}</span>
              </div>
            </div>

            <section class="pm_section" v-if="active.detail.audience">
              <h4>{{ active.detail.audienceTitle }}</h4>
              <ul class="pm_chips">
                <li v-for="(a, i) in active.detail.audience" :key="i">
                  {{ a }}
                </li>
              </ul>
            </section>

            <section class="pm_section">
              <h4>{{ t.projects.modal.overview }}</h4>
              <p>{{ active.detail.overview }}</p>
            </section>

            <section class="pm_section">
              <h4>{{ t.projects.modal.highlights }}</h4>
              <ul class="pm_checks">
                <li v-for="(h, i) in active.detail.highlights" :key="i">
                  {{ h }}
                </li>
              </ul>
            </section>

            <section class="pm_section">
              <h4>
                {{ active.detail.modulesTitle || t.projects.modal.modules }}
                <span class="pm_count">{{ active.detail.modules.length }}</span>
              </h4>
              <div class="pm_modules">
                <div v-for="(m, i) in active.detail.modules" :key="i">
                  <strong>{{ m.name }}</strong>
                  <span>{{ m.des }}</span>
                </div>
              </div>
            </section>

            <section class="pm_section pm_featured" v-if="active.detail.featured">
              <h4>{{ active.detail.featured.title }}</h4>
              <p>{{ active.detail.featured.des }}</p>
              <ul class="pm_checks">
                <li v-for="(f, i) in active.detail.featured.items" :key="i">
                  {{ f }}
                </li>
              </ul>
            </section>

            <footer class="pm_foot">
              <a href="#contact" class="pm_cta" @click.prevent="goContact">
                {{ t.projects.modal.cta }}
              </a>
            </footer>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { i18n } from "@/i18n";
import { navFuntions } from "@/navFuntions";
export default {
  name: `ProjectsComponent`,
  data() {
    return {
      activeIndex: null,
      slide: 0,
    };
  },
  computed: {
    t() {
      return i18n.m;
    },
    // Se resuelve por índice para que el modal cambie de idioma en vivo.
    active() {
      return this.activeIndex === null
        ? null
        : this.t.projects.items[this.activeIndex];
    },
  },
  methods: {
    openDetail(project) {
      this.activeIndex = this.t.projects.items.indexOf(project);
      this.slide = 0;
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", this.onKey);
    },
    closeDetail() {
      this.activeIndex = null;
      document.body.style.overflow = "";
      window.removeEventListener("keydown", this.onKey);
    },
    onKey(e) {
      if (e.key === "Escape") this.closeDetail();
    },
    goContact() {
      this.closeDetail();
      navFuntions.activeSection("contact");
    },
  },
  beforeUnmount() {
    this.closeDetail();
  },
};
</script>

<style scoped>
.edrea_tm_section .section_inner {
  display: block;
  width: 100%;
}

.edrea_tm_projects {
  width: 100%;
  flex: 1 1 100%;
}

.edrea_tm_main_title .subtitle {
  color: #a2a2a2;
  font-size: 17px;
  line-height: 1.6;
  max-width: 640px;
  margin: 15px auto 0 auto;
}

.projects_list {
  margin: 50px 0 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.projects_list li {
  margin: 0;
  padding: 0;
  list-style: none;
}

.projects_list li .list_inner {
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  overflow: hidden;
  box-sizing: border-box;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.projects_list li .list_inner:hover {
  transform: translateY(-8px);
  border-color: var(--main-color);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
}

.projects_list li .image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.projects_list li .image .main {
  position: absolute;
  inset: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transition: transform 0.5s ease;
}

.projects_list li .list_inner:hover .image .main {
  transform: scale(1.08);
}

.projects_list li .image .tag {
  position: absolute;
  left: 14px;
  bottom: 14px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  padding: 5px 12px;
  border-radius: 50px;
}

.projects_list li .image .logo_badge {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 56px;
  height: 56px;
  object-fit: contain;
  background: #fff;
  border-radius: 14px;
  padding: 5px;
  box-sizing: border-box;
  box-shadow: 0 6px 18px rgba(13, 27, 58, 0.2);
}

.projects_list li .content {
  padding: 26px 28px 30px 28px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.projects_list li .content .title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
}

.projects_list li .content .text {
  color: #a2a2a2;
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 18px;
  flex: 1;
}

.projects_list li .modules {
  margin-bottom: 18px;
}

.projects_list li .modules .refs_label {
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #6d6d6d;
  margin-bottom: 10px;
}

.projects_list li .modules_list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.projects_list li .modules_list li {
  font-size: 12px;
  line-height: 1.4;
  color: #a2a2a2;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 3px 10px;
  border-radius: 50px;
}

.projects_list li .refs {
  border-top: 1px dashed rgba(255, 255, 255, 0.12);
  padding-top: 16px;
}

.projects_list li .refs_label {
  display: block;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #6d6d6d;
  margin-bottom: 10px;
}

.projects_list li .refs_links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.projects_list li .refs_links a {
  font-size: 13px;
  color: #fff;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 5px 14px;
  border-radius: 50px;
  transition: all 0.3s ease;
}

.projects_list li .refs_links a:hover {
  background: var(--main-color);
  border-color: var(--main-color);
}

.projects_list li .list_inner.clickable {
  cursor: pointer;
}

.projects_list li .detail_btn {
  align-self: flex-start;
  margin-bottom: 18px;
  padding: 0;
  background: none;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--main-color);
  cursor: pointer;
}

/* ===== MODAL DE PROYECTO ===== */
.project_modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(13, 27, 58, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

.pm_box {
  position: relative;
  width: 100%;
  max-width: 980px;
  background: #fff;
  border-radius: 18px;
  padding: 40px;
  box-sizing: border-box;
  box-shadow: 0 30px 80px rgba(13, 27, 58, 0.35);
  color: var(--dm-text, #4a5878);
  text-align: left;
}

.pm_close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid var(--dm-border, #e2eaf6);
  background: #fff;
  color: var(--dm-navy, #0d1b3a);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pm_close:hover {
  background: var(--main-color);
  border-color: var(--main-color);
  color: #fff;
}

.pm_head {
  display: flex;
  align-items: center;
  gap: 22px;
  padding-right: 40px;
}

.pm_logo {
  flex: 0 0 auto;
  width: 96px;
  height: 96px;
  object-fit: contain;
  background: #fff;
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 18px;
  padding: 8px;
  box-sizing: border-box;
}

.pm_tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: var(--main-color);
  background: var(--dm-blue-soft, rgba(33, 118, 255, 0.1));
  padding: 5px 12px;
  border-radius: 50px;
}

.pm_title {
  font-size: 32px;
  font-weight: 700;
  color: var(--dm-navy, #0d1b3a);
  margin: 12px 0 4px 0;
}

.pm_subtitle {
  font-size: 17px;
  color: var(--dm-text, #4a5878);
  margin: 0;
}

.pm_gallery {
  margin-top: 28px;
}

.pm_main {
  margin: 0;
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 12px;
  overflow: hidden;
  background: var(--dm-surface, #f5f8fd);
}

.pm_main img {
  display: block;
  width: 100%;
  height: auto;
}

.pm_main figcaption {
  font-size: 13px;
  padding: 10px 16px;
  border-top: 1px solid var(--dm-border, #e2eaf6);
  color: var(--dm-text, #4a5878);
}

.pm_thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.pm_thumbs button {
  width: 140px;
  height: 84px;
  padding: 0;
  border: 2px solid var(--dm-border, #e2eaf6);
  border-radius: 8px;
  overflow: hidden;
  background: none;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s ease;
}

.pm_thumbs button.current,
.pm_thumbs button:hover {
  border-color: var(--main-color);
  opacity: 1;
}

.pm_thumbs img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
}

.pm_stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 28px;
}

.pm_stats div {
  background: var(--dm-surface, #f5f8fd);
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 12px;
  padding: 18px 20px;
}

.pm_stats strong {
  display: block;
  font-size: 26px;
  color: var(--dm-navy, #0d1b3a);
}

.pm_stats span {
  font-size: 13px;
}

.pm_section {
  margin-top: 32px;
}

.pm_section h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--dm-navy, #0d1b3a);
  margin: 0 0 14px 0;
}

.pm_section p {
  font-size: 15px;
  line-height: 1.7;
  margin: 0;
}

.pm_count {
  font-size: 12px;
  color: var(--main-color);
  background: var(--dm-blue-soft, rgba(33, 118, 255, 0.1));
  padding: 2px 8px;
  border-radius: 50px;
}

.pm_checks {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 24px;
}

.pm_checks li {
  position: relative;
  padding-left: 26px;
  font-size: 15px;
  line-height: 1.5;
}

.pm_checks li::before {
  content: "✓";
  position: absolute;
  left: 0;
  top: 0;
  font-weight: 700;
  color: var(--main-color);
}

.pm_chips {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pm_chips li {
  font-size: 14px;
  font-weight: 500;
  color: var(--dm-navy, #0d1b3a);
  background: var(--dm-blue-soft, rgba(33, 118, 255, 0.1));
  padding: 6px 14px;
  border-radius: 50px;
}

.pm_modules {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.pm_modules div {
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 10px;
  padding: 14px;
}

.pm_modules strong {
  display: block;
  font-size: 14px;
  color: var(--dm-navy, #0d1b3a);
  margin-bottom: 4px;
}

.pm_modules span {
  font-size: 13px;
  line-height: 1.5;
}

.pm_featured {
  background: var(--dm-surface, #f5f8fd);
  border: 1px solid var(--dm-border, #e2eaf6);
  border-radius: 12px;
  padding: 24px;
}

.pm_featured p {
  margin-bottom: 16px;
}

.pm_foot {
  margin-top: 32px;
  text-align: center;
}

.pm_cta {
  display: inline-block;
  font-weight: 600;
  color: #fff;
  background: var(--main-color);
  padding: 12px 28px;
  border-radius: 50px;
  text-decoration: none;
  transition: background 0.2s ease;
}

.pm_cta:hover {
  color: #fff;
  background: var(--dm-blue-dark, #1558c9);
}

.pmodal-enter-active,
.pmodal-leave-active {
  transition: opacity 0.25s ease;
}

.pmodal-enter-from,
.pmodal-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .pm_modules {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .project_modal {
    padding: 16px;
  }
  .pm_box {
    padding: 28px 20px;
  }
  .pm_title {
    font-size: 24px;
  }
  .pm_head {
    align-items: flex-start;
    gap: 14px;
  }
  .pm_logo {
    width: 64px;
    height: 64px;
    border-radius: 14px;
  }
  .pm_stats,
  .pm_checks {
    grid-template-columns: 1fr;
  }
  .pm_thumbs button {
    width: 96px;
    height: 58px;
  }
}

@media (max-width: 1040px) {
  .projects_list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .projects_list {
    grid-template-columns: 1fr;
  }
}
</style>
